import type { ApiClient } from "./client";

export interface Tenant {
  id: string;
  slug: string;
  name: string;
  bot_display_name: string;
  retention_days: number;
  role?: "owner" | "admin" | "member";
  onboarding_step?: number;
  onboarding_completed?: boolean;
  created_at: string;
}

export interface UpdateTenantRequest {
  name?: string;
  bot_display_name?: string;
  retention_days?: 30 | 60 | 90;
}

export interface CreateTenantRequest {
  name: string;
  slug: string;
  summary_language?: "id" | "en" | "bilingual";
}

export interface CreateInviteRequest {
  email: string;
  role: "admin" | "member";
}

export interface InviteResponse {
  id: string;
  email: string;
  role: string;
  expires_at: string;
}

export interface TenantSettings {
  tenant_id: string;
  summary_language: "id" | "en" | "bilingual";
  calendar_auto_attach: boolean;
  updated_at: string;
}

export const tenantsApi = (client: ApiClient) => ({
  list: () => client.get<{ tenants: Tenant[] }>("/api/v1/tenants"),
  create: (req: CreateTenantRequest) => client.post<Tenant>("/api/v1/tenants", req),
  get: (id: string) => client.get<Tenant>(`/api/v1/tenants/${id}`),
  update: (id: string, req: UpdateTenantRequest) =>
    client.patch<Tenant>(`/api/v1/tenants/${id}`, req),
  getSettings: (id: string) => client.get<TenantSettings>(`/api/v1/tenants/${id}/settings`),
  updateSettings: (
    id: string,
    body: { summary_language?: "id" | "en" | "bilingual"; calendar_auto_attach?: boolean },
  ) => client.patch<TenantSettings>(`/api/v1/tenants/${id}/settings`, body),
  updateOnboardingStep: (id: string, step: number) =>
    client.patch<{ step: number }>(`/api/v1/tenants/${id}/onboarding-step`, { step }),
  completeOnboarding: (id: string) =>
    client.post<{ onboarding_completed: boolean }>(`/api/v1/tenants/${id}/onboarding/complete`),
  createInvite: (tenantId: string, req: CreateInviteRequest) =>
    client.post<InviteResponse>(`/api/v1/tenants/${tenantId}/invites`, req),
  acceptInvite: (token: string) => client.post<Tenant>("/api/v1/invites/accept", { token }),
  listMembers: (tenantId: string) =>
    client.get<{ members: Array<{ id: string; email: string; name: string; role: string }> }>(
      `/api/v1/tenants/${tenantId}/members`
    ),
});
